// AppShell — wraps every customer screen with:
//   * top bar (brand wordmark + sign-in link)
//   * optional progress bar
//   * page body slot
//   * optional sticky footer slot (mobile CTAs)

import React from 'react';
import { Link } from 'react-router-dom';
import { ProgressBar } from './ProgressBar';
import { useSession } from '../lib/useSession';

export function AppShell({
  children,
  footer,
  progress, // { total, current, label } | null
  hideNav = false,
}) {
  const { user } = useSession();

  return (
    <div className="min-h-screen flex flex-col relative">
      {!hideNav && (
        <header
          className="sticky top-0 z-50"
          style={{
            background: 'rgba(247,240,226,0.92)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div className="container-app flex items-center justify-between gap-3 py-4">
            <Link
              to="/"
              className="flex items-center gap-2 sm:gap-3 min-w-0"
              data-testid="brand-home-link"
            >
              <span
                className="font-display uppercase text-2xl sm:text-3xl leading-none whitespace-nowrap"
                style={{ letterSpacing: '0.04em' }}
              >
                Eat <span style={{ color: 'var(--orange)' }}>This</span>
              </span>
              <span
                className="eyebrow whitespace-nowrap hidden sm:inline-flex"
                style={{ color: 'var(--text-dim)' }}
                data-testid="brand-meal-planner-label"
              >
                Meal Planner
              </span>
            </Link>

            <nav className="flex items-center gap-4 sm:gap-6 shrink-0">
              <a
                href="https://www.eatthisthewoodlands.com"
                className="eyebrow hidden sm:inline-flex whitespace-nowrap hover:opacity-70 transition-opacity"
                style={{ color: 'var(--ink)' }}
                data-testid="nav-back-to-eatthis-link"
              >
                Back to Eat This
              </a>
              <Link
                to="/trainer/apply"
                className="eyebrow hidden sm:inline-flex whitespace-nowrap hover:opacity-70 transition-opacity"
                style={{ color: 'var(--ink)' }}
                data-testid="nav-trainer-program-link"
              >
                Trainer Program
              </Link>
              {user ? (
                <Link
                  to="/account"
                  className="eyebrow whitespace-nowrap"
                  style={{ color: 'var(--ink)' }}
                  data-testid="nav-account-link"
                >
                  Account
                </Link>
              ) : (
                <Link
                  to="/sign-in"
                  className="eyebrow whitespace-nowrap"
                  style={{ color: 'var(--ink)' }}
                  data-testid="nav-signin-link"
                >
                  Sign in
                </Link>
              )}
            </nav>
          </div>

          {/* Mobile-only compact top link back to the main site */}
          <a
            href="https://www.eatthisthewoodlands.com"
            className="sm:hidden block text-center py-2 eyebrow"
            style={{
              background: 'var(--ink)',
              color: 'var(--cream)',
              fontSize: '11px',
              letterSpacing: '0.28em',
            }}
            data-testid="nav-back-to-eatthis-link-mobile"
          >
            ← Back to Eat This
          </a>

          {progress && (
            <div className="container-app pb-3">
              <ProgressBar
                total={progress.total}
                current={progress.current}
                label={progress.label}
              />
            </div>
          )}
        </header>
      )}

      <main className="flex-1 container-app py-8 md:py-14 relative z-[2]">
        {children}
      </main>

      {footer && <div className="sticky-footer container-app">{footer}</div>}
    </div>
  );
}

export default AppShell;
